/**
 * AutoAlertPublisherManagerEmailPage — manager email-alert rules + recent-emails feed.
 *
 * Top card: "New alert rule" form (trigger / manager email / template).
 * Bottom card: recent-emails feed with status filter + mark-sent toggle.
 * GREEN-only accent palette (no emerald/blue/orange/purple/dark/emoji).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAutoAlertPublisherManagerEmailList,
	useAutoAlertPublisherManagerEmailCreate,
	useAutoAlertPublisherManagerEmailMarkSent,
} from "./hooks";
import type { AlertStatus } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const LABEL =
	"text-xs font-semibold uppercase tracking-wider text-zinc-500";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const FILTER_ACTIVE =
	"rounded-[6px] border border-green-700 bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-800";
const FILTER_IDLE =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50";

const STATUS_PILL: Record<AlertStatus, string> = {
	pending:
		"rounded-full border border-zinc-300 bg-white px-2 py-0.5 text-[11px] font-medium text-zinc-700",
	sent: "rounded-full border border-green-700 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800",
	failed:
		"rounded-full border border-zinc-400 bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-700",
};

const STATUSES: Array<{ key: AlertStatus | "all"; label: string }> = [
	{ key: "all", label: "All" },
	{ key: "pending", label: "Pending" },
	{ key: "sent", label: "Sent" },
	{ key: "failed", label: "Failed" },
];

function fmtTs(ts: number | null | undefined): string {
	if (!ts || !Number.isFinite(ts)) return "-";
	const d = new Date(ts * 1000);
	const iso = d.toISOString();
	return iso.replace("T", " ").slice(0, 19);
}

function previewBody(body: string): string {
	const trimmed = body.replace(/\s+/g, " ").trim();
	if (trimmed.length <= 90) return trimmed;
	return `${trimmed.slice(0, 90)}...`;
}

export function AutoAlertPublisherManagerEmailPage() {
	const [filter, setFilter] = useState<AlertStatus | "all">("all");
	const listQuery = useAutoAlertPublisherManagerEmailList(
		filter === "all" ? undefined : filter,
	);
	const createMut = useAutoAlertPublisherManagerEmailCreate();
	const markSentMut = useAutoAlertPublisherManagerEmailMarkSent();

	const [eventId, setEventId] = useState("");
	const [managerEmail, setManagerEmail] = useState("");
	const [eventType, setEventType] = useState("");
	const [subject, setSubject] = useState("");
	const [body, setBody] = useState("");

	const canSubmit = useMemo(
		() =>
			eventId.trim().length > 0 &&
			managerEmail.trim().length > 0 &&
			eventType.trim().length > 0 &&
			subject.trim().length > 0 &&
			body.trim().length > 0 &&
			!createMut.isPending,
		[eventId, managerEmail, eventType, subject, body, createMut.isPending],
	);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!canSubmit) return;
		createMut.mutate(
			{
				event_id: eventId.trim(),
				manager_email: managerEmail.trim(),
				event_type: eventType.trim(),
				subject: subject.trim(),
				body: body.trim(),
			},
			{
				onSuccess: () => {
					setEventId("");
					setSubject("");
					setBody("");
				},
			},
		);
	};

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="auto-alert-publisher-manager-email-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className={LABEL}>auto-alert-publisher-manager-email</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Publisher manager email alerts
				</h1>
				<p className="text-sm text-zinc-600">
					Queue manager-notification emails for upstream events. Ingestion is
					idempotent on event_id; duplicates are deduped server-side.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="New alert rule"
				data-testid="auto-alert-publisher-manager-email-create-card"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					New alert rule
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Trigger + recipient + template. `event_id` is the idempotency key.
				</p>
				<form
					onSubmit={handleCreate}
					className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="auto-alert-publisher-manager-email-create-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Event id</span>
						<input
							type="text"
							className={INPUT}
							placeholder="evt_abc123"
							value={eventId}
							onChange={(e) => setEventId(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-alert-publisher-manager-email-event-id"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Trigger (event_type)</span>
						<input
							type="text"
							className={INPUT}
							placeholder="affiliate.signup"
							value={eventType}
							onChange={(e) => setEventType(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-alert-publisher-manager-email-event-type"
						/>
					</label>
					<label className="flex flex-col gap-1 sm:col-span-2">
						<span className={LABEL}>Manager email</span>
						<input
							type="email"
							className={INPUT}
							placeholder="manager@publisher.example"
							value={managerEmail}
							onChange={(e) => setManagerEmail(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-alert-publisher-manager-email-manager-email"
						/>
					</label>
					<label className="flex flex-col gap-1 sm:col-span-2">
						<span className={LABEL}>Template subject</span>
						<input
							type="text"
							className={INPUT}
							placeholder="New affiliate signup for your publisher"
							value={subject}
							onChange={(e) => setSubject(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-alert-publisher-manager-email-subject"
						/>
					</label>
					<label className="flex flex-col gap-1 sm:col-span-2">
						<span className={LABEL}>Template body</span>
						<textarea
							className={`${INPUT} min-h-[96px] resize-y`}
							placeholder="Hi {{manager_name}}, a new affiliate just joined your publisher program..."
							value={body}
							onChange={(e) => setBody(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-alert-publisher-manager-email-body"
						/>
					</label>
					<div className="sm:col-span-2 flex items-center justify-between">
						<label className="inline-flex items-center gap-2 text-sm text-zinc-700">
							<input
								type="checkbox"
								defaultChecked
								className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600"
								data-testid="auto-alert-publisher-manager-email-enabled"
							/>
							Enabled
						</label>
						<button
							type="submit"
							className={BTN}
							disabled={!canSubmit}
							data-testid="auto-alert-publisher-manager-email-submit"
						>
							{createMut.isPending ? "Queueing." : "Queue alert"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p
						className="mt-3 text-xs text-zinc-800"
						role="alert"
						data-testid="auto-alert-publisher-manager-email-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
				{createMut.data && createMut.data.deduped ? (
					<p
						className="mt-3 text-xs text-zinc-600"
						data-testid="auto-alert-publisher-manager-email-create-deduped"
					>
						Existing alert matched event_id; no duplicate was queued.
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Recent emails feed"
				data-testid="auto-alert-publisher-manager-email-list-card"
			>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Recent emails
					</h2>
					<div
						className="flex flex-wrap gap-1.5"
						role="tablist"
						aria-label="Filter by status"
						data-testid="auto-alert-publisher-manager-email-filter"
					>
						{STATUSES.map((s) => (
							<button
								key={s.key}
								type="button"
								role="tab"
								aria-selected={filter === s.key}
								className={filter === s.key ? FILTER_ACTIVE : FILTER_IDLE}
								onClick={() => setFilter(s.key)}
								data-testid={`auto-alert-publisher-manager-email-filter-${s.key}`}
							>
								{s.label}
							</button>
						))}
					</div>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="auto-alert-publisher-manager-email-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-4 text-sm text-zinc-800"
						role="alert"
						data-testid="auto-alert-publisher-manager-email-list-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="auto-alert-publisher-manager-email-empty"
					>
						No alerts yet. Queue one above, or wait for an upstream event.
					</p>
				) : (
					<ul
						className="mt-4 divide-y divide-zinc-200"
						data-testid="auto-alert-publisher-manager-email-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between"
								data-testid={`auto-alert-publisher-manager-email-row-${row.id}`}
							>
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-center gap-2">
										<span className={STATUS_PILL[row.status]}>
											{row.status}
										</span>
										<span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
											{row.event_type}
										</span>
										<span className="font-mono text-[11px] text-zinc-500">
											{row.event_id}
										</span>
									</div>
									<p className="mt-1 truncate text-sm font-medium text-zinc-900">
										{row.subject}
									</p>
									<p className="mt-0.5 text-xs text-zinc-600">
										{previewBody(row.body)}
									</p>
									<p className="mt-1 text-[11px] text-zinc-500">
										to {row.manager_email}
										<span className="mx-1.5 text-zinc-300">|</span>
										created {fmtTs(row.created_at)}
										{row.sent_at ? (
											<>
												<span className="mx-1.5 text-zinc-300">|</span>
												sent {fmtTs(row.sent_at)}
											</>
										) : null}
									</p>
								</div>
								<div className="flex shrink-0 items-center gap-2">
									{row.status === "pending" ? (
										<button
											type="button"
											className={BTN_GHOST}
											onClick={() => markSentMut.mutate(row.id)}
											disabled={markSentMut.isPending}
											data-testid={`auto-alert-publisher-manager-email-mark-sent-${row.id}`}
										>
											{markSentMut.isPending &&
											markSentMut.variables === row.id
												? "Marking."
												: "Mark sent"}
										</button>
									) : (
										<span className="text-[11px] text-zinc-500">
											{row.status === "sent" ? "Delivered" : "Locked"}
										</span>
									)}
								</div>
							</li>
						))}
					</ul>
				)}
				{markSentMut.error ? (
					<p
						className="mt-3 text-xs text-zinc-800"
						role="alert"
						data-testid="auto-alert-publisher-manager-email-marksent-error"
					>
						{markSentMut.error.message}
					</p>
				) : null}
			</section>
		</main>
	);
}

export default AutoAlertPublisherManagerEmailPage;
